/**
 * 🏠 Property Details Content
 * Detalhe de registro de imóvel — redesign Design System.
 *
 * Anatomia (ancorada no mockup databases-property-detail.html):
 *  - Toolbar 3-zonas (DS 3.3): breadcrumb à esquerda (nunca seta-voltar) +
 *    ações primárias (Editar / Excluir) à direita.
 *  - Header de identidade do imóvel (.prof): avatar (ícone home, slate) + título +
 *    subtítulo (ref · tipo · localização) + status + "Copiar referência".
 *  - Grid: coluna principal (galeria + características + descrição + comodidades +
 *    localização) e sidebar (oportunidades + tipo&referência + construção + tags +
 *    metadados). Empilha no mobile.
 *
 * A galeria mantém navegação, contador, thumbnails e lightbox em tela cheia.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type {
  DatabaseDocumentResponse,
  DatabasePropertyData,
  PropertyType,
  BusinessOpportunity,
  BusinessOpportunityType,
  OpportunityStatus,
  PropertyItemStatus,
} from '@troia-v3/types';
import { Breadcrumb } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { useDatabase } from '../../hooks/useDatabases';

interface PropertyDetailsContentProps {
  document: DatabaseDocumentResponse<DatabasePropertyData>;
  databaseId: string;
  onEdit?: () => void;
  onDelete?: () => void;
  onExportPdf?: () => void;
}

/**
 * Formata preço em formato compacto brasileiro
 */
const formatPrice = (value: number): string => {
  if (value >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1).replace('.', ',')}M`;
  }
  if (value >= 1000) {
    return `R$ ${Math.round(value / 1000)}mil`;
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Formata preço completo
 */
const formatFullPrice = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Retorna label do tipo de propriedade
 */
const getPropertyTypeLabel = (type: PropertyType): string => {
  const labels: Record<PropertyType, string> = {
    house: 'Casa',
    apartment: 'Apartamento',
    commercial: 'Comercial',
    land: 'Terreno',
    farm: 'Fazenda',
    warehouse: 'Galpão',
    office: 'Escritório',
    studio: 'Studio',
    penthouse: 'Cobertura',
    townhouse: 'Sobrado',
  };
  return labels[type] || type;
};

/**
 * Retorna label do tipo de oportunidade
 */
const getOpportunityTypeLabel = (type: BusinessOpportunityType): string => {
  const labels: Record<BusinessOpportunityType, string> = {
    sale: 'Venda',
    rent: 'Aluguel',
    seasonal_rent: 'Temporada',
    daily_rent: 'Diária',
    lease: 'Arrendamento',
    exchange: 'Permuta',
    donation: 'Doação',
    auction: 'Leilão',
  };
  return labels[type] || type;
};

/**
 * Retorna label do status da oportunidade
 */
const getOpportunityStatusLabel = (status: OpportunityStatus): string => {
  const labels: Record<OpportunityStatus, string> = {
    active: 'Ativa',
    inactive: 'Inativa',
    reserved: 'Reservada',
    in_negotiation: 'Em negociação',
    completed: 'Concluída',
    cancelled: 'Cancelada',
  };
  return labels[status] || status;
};

/**
 * Retorna cor do status da oportunidade
 */
const getOpportunityStatusColor = (status: OpportunityStatus): { bg: string; text: string } => {
  const colors: Record<OpportunityStatus, { bg: string; text: string }> = {
    active: { bg: 'var(--theme-success-background)', text: 'var(--theme-success-color)' },
    inactive: { bg: 'var(--theme-error-background)', text: 'var(--theme-error-color)' },
    reserved: { bg: 'var(--theme-warning-background)', text: 'var(--theme-warning-color)' },
    in_negotiation: { bg: 'var(--theme-info-background)', text: 'var(--theme-info-color)' },
    completed: { bg: 'var(--theme-primary-background)', text: 'var(--theme-primary)' },
    cancelled: { bg: 'var(--theme-error-background)', text: 'var(--theme-error-color)' },
  };
  return colors[status] || { bg: 'var(--theme-card-background)', text: 'var(--theme-muted-text)' };
};

/**
 * Retorna label do status do imóvel
 */
const getItemStatusLabel = (status: PropertyItemStatus): string => {
  const labels: Record<PropertyItemStatus, string> = {
    available: 'Disponível',
    unavailable: 'Indisponível',
    under_construction: 'Em Construção',
    launch: 'Lançamento',
    reserved: 'Reservado',
    archived: 'Arquivado',
  };
  return labels[status] || status;
};

/**
 * Retorna cor do status do imóvel
 */
const getItemStatusColor = (status: PropertyItemStatus): { bg: string; text: string } => {
  const colors: Record<PropertyItemStatus, { bg: string; text: string }> = {
    available: { bg: 'var(--theme-success-background)', text: 'var(--theme-success-color)' },
    unavailable: { bg: 'var(--theme-error-background)', text: 'var(--theme-error-color)' },
    under_construction: { bg: 'var(--theme-warning-background)', text: 'var(--theme-warning-color)' },
    // Lançamento é estado público e positivo, como "Disponível" — daí a mesma
    // família de cor, sem inventar token novo.
    launch: { bg: 'var(--theme-success-background)', text: 'var(--theme-success-color)' },
    reserved: { bg: 'var(--theme-info-background)', text: 'var(--theme-info-color)' },
    archived: { bg: 'var(--theme-muted-text)', text: '#ffffff' },
  };
  return colors[status] || { bg: 'var(--theme-card-background)', text: 'var(--theme-muted-text)' };
};

/**
 * Retorna label da condição de construção
 */
const getConditionLabel = (condition: string): string => {
  const labels: Record<string, string> = {
    new: 'Novo',
    excellent: 'Excelente',
    good: 'Bom',
    needs_renovation: 'Precisa Reforma',
    under_renovation: 'Em Reforma',
  };
  return labels[condition] || 'Em Reforma';
};

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--theme-card-background)',
  border: '1px solid var(--theme-card-border)',
};

/**
 * Card de seção com cabeçalho (ícone + título) — padrão DS.
 */
const Section: React.FC<{
  icon: IconName;
  title: string;
  children: React.ReactNode;
  testId?: string;
}> = ({ icon, title, children, testId }) => (
  <section className="rounded-xl overflow-hidden" style={cardStyle} data-testid={testId}>
    <div
      className="flex items-center gap-2 px-4 py-3 border-b"
      style={{ borderColor: 'var(--theme-card-border)' }}
    >
      <Icon name={icon} className="w-4 h-4 flex-none" style={{ color: 'var(--theme-muted-text)' }} />
      <span className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>
        {title}
      </span>
    </div>
    <div className="p-4">{children}</div>
  </section>
);

/**
 * Linha chave/valor da sidebar (definition list).
 * `divider` desenha uma borda superior (separador entre linhas).
 */
const DlRow: React.FC<{ label: string; children: React.ReactNode; divider?: boolean }> = ({
  label,
  children,
  divider,
}) => (
  <div
    className={`flex items-center gap-3 py-1.5 text-sm ${divider ? 'border-t mt-1.5 pt-2.5' : ''}`}
    style={divider ? { borderColor: 'var(--theme-card-border)' } : undefined}
  >
    <span
      className="w-24 flex-none text-xs font-semibold"
      style={{ color: 'var(--theme-muted-text)' }}
    >
      {label}
    </span>
    <div
      className="flex-1 min-w-0 font-medium flex items-center gap-1.5"
      style={{ color: 'var(--theme-card-text)' }}
    >
      {children}
    </div>
  </div>
);

export const PropertyDetailsContent: React.FC<PropertyDetailsContentProps> = ({
  document,
  databaseId,
  onEdit,
  onDelete,
  onExportPdf,
}) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageFullscreen, setIsImageFullscreen] = useState(false);

  const property = document.data;
  const photos = property.media?.photos || [];
  const hasPhotos = photos.length > 0;

  // Nome da base para o breadcrumb (rota de módulo — usuário já tem acesso).
  const { data: database } = useDatabase(databaseId);

  const itemStatusColor = getItemStatusColor(property.itemStatus);

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  const handleBackToDatabases = () => navigate('/databases');
  const handleBackToDatabase = () => navigate(`/databases/${databaseId}`);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Subtítulo de identidade: referência · tipo · localização
  const subParts = [
    property.reference,
    getPropertyTypeLabel(property.propertyType),
    `${property.address.neighborhood}, ${property.address.city}/${property.address.state}`,
  ].filter(Boolean);

  // Características em tiles (só as definidas) — combina básicas + adicionais.
  const f = property.features;
  const featureTiles: { value: string; label: string }[] = [];
  if (f.bedrooms !== undefined)
    featureTiles.push({ value: String(f.bedrooms), label: f.bedrooms === 1 ? 'Quarto' : 'Quartos' });
  if (f.bathrooms !== undefined)
    featureTiles.push({
      value: String(f.bathrooms),
      label: f.bathrooms === 1 ? 'Banheiro' : 'Banheiros',
    });
  if (f.suites !== undefined)
    featureTiles.push({ value: String(f.suites), label: f.suites === 1 ? 'Suíte' : 'Suítes' });
  if (f.parkingSpaces !== undefined)
    featureTiles.push({
      value: String(f.parkingSpaces),
      label: f.parkingSpaces === 1 ? 'Vaga' : 'Vagas',
    });
  if (f.totalArea !== undefined)
    featureTiles.push({ value: `${f.totalArea} m²`, label: 'Área total' });
  if (f.builtArea !== undefined)
    featureTiles.push({ value: `${f.builtArea} m²`, label: 'Construída' });
  if (f.privateArea !== undefined)
    featureTiles.push({ value: `${f.privateArea} m²`, label: 'Privativa' });
  if (f.floors !== undefined)
    featureTiles.push({ value: String(f.floors), label: f.floors === 1 ? 'Andar' : 'Andares' });

  const hasConstruction =
    !!property.construction && (!!property.construction.year || !!property.construction.condition);

  return (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
      data-testid="property-details-content"
    >
      {/* Toolbar 3 zonas (DS 3.3): breadcrumb à esquerda, ações à direita */}
      <header
        className="flex-none h-14 flex items-center gap-3 px-4 sm:px-6 border-b"
        style={{
          backgroundColor: 'var(--theme-header-background)',
          borderColor: 'var(--theme-header-border)',
        }}
      >
        {/* Breadcrumb shared (F25-31; F26: hover morto corrigido pelo shared) */}
        <Breadcrumb
          items={[
            { label: 'Bases de dados', onNavigate: handleBackToDatabases, testid: 'property-breadcrumb-databases' },
            { label: database?.name || 'Base', onNavigate: handleBackToDatabase, testid: 'property-breadcrumb-database' },
            { label: property.reference || property.title, testid: 'property-breadcrumb-current' },
          ]}
          mobileLegend={`Bases de dados · ${database?.name || 'Base'}`}
          data-testid="property-breadcrumb"
        />

        <div className="flex items-center gap-2 flex-none">
          {onExportPdf && (
            <button
              onClick={onExportPdf}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium border transition-colors"
              style={{
                borderColor: 'var(--theme-header-border)',
                color: 'var(--theme-header-text)',
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-header-button-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title="Exportar PDF"
              data-testid="property-export-pdf-button"
            >
              <Icon name="download" className="w-4 h-4" />
              <span className="hidden sm:inline">Exportar PDF</span>
            </button>
          )}
          {onEdit && (
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold transition-colors"
              style={{
                backgroundColor: 'var(--theme-button-primary)',
                color: 'var(--theme-button-primary-text, #ffffff)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-primary-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-primary)';
              }}
              data-testid="property-edit-button"
            >
              <Icon name="edit" className="w-4 h-4" />
              <span className="hidden sm:inline">Editar</span>
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors"
              style={{ color: 'var(--theme-error-color)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-error-background)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title="Excluir"
              data-testid="property-delete-button"
            >
              <Icon name="trash" className="w-[18px] h-[18px]" />
            </button>
          )}
        </div>
      </header>

      {/* Conteúdo */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
          {/* Header de identidade do imóvel */}
          <div
            className="flex items-start gap-4 rounded-xl p-4 sm:p-5 mb-4 md:mb-6 flex-wrap sm:flex-nowrap"
            style={cardStyle}
          >
            <div
              className="flex-none w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: '#64748b' }}
            >
              <Icon name="home" className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: '#ffffff' }} />
            </div>
            <div className="flex-1 min-w-0">
              <h1
                className="text-lg sm:text-xl font-bold tracking-tight truncate"
                style={{ color: 'var(--theme-card-text)' }}
                title={property.title}
              >
                {property.title}
              </h1>
              <p className="text-sm mt-0.5 truncate" style={{ color: 'var(--theme-muted-text)' }}>
                {subParts.join(' · ')}
              </p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: itemStatusColor.bg, color: itemStatusColor.text }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: 'currentColor' }}
                  />
                  {getItemStatusLabel(property.itemStatus)}
                </span>
              </div>
            </div>
            {property.reference && (
              <div className="flex items-center gap-2 flex-none w-full sm:w-auto">
                <button
                  onClick={() => copyToClipboard(property.reference!)}
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium border transition-colors"
                  style={{
                    borderColor: 'var(--theme-card-border)',
                    color: 'var(--theme-card-text)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  title="Copiar referência"
                  data-testid="property-copy-reference"
                >
                  <Icon name="copy" className="w-4 h-4" />
                  <span className="hidden sm:inline">Copiar referência</span>
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-4 md:gap-6 items-start">
            {/* Coluna principal */}
            <div className="flex flex-col gap-4 md:gap-6 min-w-0">
              {/* Galeria */}
              <div className="rounded-xl overflow-hidden p-4" style={cardStyle}>
                {hasPhotos ? (
                  <div
                    className="relative rounded-xl overflow-hidden"
                    style={{ backgroundColor: 'var(--theme-page-background)' }}
                  >
                    <img
                      src={photos[currentImageIndex]}
                      alt={`${property.title} - Foto ${currentImageIndex + 1}`}
                      className="w-full h-72 sm:h-[340px] object-cover cursor-zoom-in"
                      onClick={() => setIsImageFullscreen(true)}
                      data-testid="property-gallery-open"
                    />

                    {/* Navegação */}
                    {photos.length > 1 && (
                      <>
                        <button
                          onClick={handlePrevImage}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center bg-black/40 text-white hover:bg-black/60 transition-colors"
                          data-testid="property-gallery-prev"
                          aria-label="Foto anterior"
                        >
                          <Icon name="chevron-left" className="w-5 h-5" />
                        </button>
                        <button
                          onClick={handleNextImage}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center bg-black/40 text-white hover:bg-black/60 transition-colors"
                          data-testid="property-gallery-next"
                          aria-label="Próxima foto"
                        >
                          <Icon name="chevron-right" className="w-5 h-5" />
                        </button>
                      </>
                    )}

                    {/* Contador */}
                    <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-black/55 text-white text-xs font-semibold">
                      {currentImageIndex + 1} / {photos.length}
                    </div>

                    {/* Tela cheia */}
                    <button
                      onClick={() => setIsImageFullscreen(true)}
                      className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center bg-black/40 text-white hover:bg-black/60 transition-colors"
                      title="Ver em tela cheia"
                      data-testid="property-gallery-fullscreen"
                      aria-label="Ver em tela cheia"
                    >
                      <Icon name="maximize" className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div
                    className="h-72 sm:h-[340px] rounded-xl flex flex-col items-center justify-center gap-2"
                    style={{ backgroundColor: 'var(--theme-page-background)' }}
                  >
                    <Icon name="image" className="w-10 h-10" style={{ color: 'var(--theme-muted-text)' }} />
                    <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                      Sem imagens
                    </span>
                  </div>
                )}

                {/* Thumbnails */}
                {photos.length > 1 && (
                  <div className="flex gap-2 mt-3 overflow-x-auto">
                    {photos.map((photo, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all"
                        style={{
                          border: '2px solid',
                          borderColor:
                            index === currentImageIndex ? 'var(--theme-primary)' : 'transparent',
                        }}
                        data-testid={`property-gallery-thumb-${index}`}
                        aria-label={`Miniatura ${index + 1}`}
                      >
                        <img
                          src={photo}
                          alt={`Miniatura ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Características */}
              {featureTiles.length > 0 && (
                <Section icon="grid" title="Características" testId="property-features">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {featureTiles.map((tile) => (
                      <div
                        key={tile.label}
                        className="rounded-xl p-3 text-center"
                        style={{ border: '1px solid var(--theme-card-border)' }}
                      >
                        <div
                          className="text-lg font-extrabold tracking-tight"
                          style={{ color: 'var(--theme-card-text)' }}
                        >
                          {tile.value}
                        </div>
                        <div
                          className="text-xs font-semibold mt-0.5"
                          style={{ color: 'var(--theme-muted-text)' }}
                        >
                          {tile.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Descrição */}
              <Section icon="file-text" title="Descrição" testId="property-description">
                <p
                  className="text-sm whitespace-pre-wrap leading-relaxed"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  {property.description || 'Sem descrição disponível.'}
                </p>
              </Section>

              {/* Comodidades */}
              {property.amenities && property.amenities.length > 0 && (
                <Section icon="grid" title="Comodidades" testId="property-amenities">
                  <div className="flex flex-wrap gap-2">
                    {property.amenities.map((amenity, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                        style={{
                          backgroundColor: 'var(--theme-success-background)',
                          color: 'var(--theme-success-color)',
                        }}
                      >
                        <Icon name="check" className="w-3.5 h-3.5" />
                        {amenity}
                      </span>
                    ))}
                  </div>
                </Section>
              )}

              {/* Localização */}
              <Section icon="map-pin" title="Localização" testId="property-location">
                {/* Mapa (placeholder) — só quando há coordenadas */}
                {property.address.coordinates && (
                  <div
                    className="h-44 rounded-xl flex items-center justify-center mb-3"
                    style={{
                      background:
                        'repeating-linear-gradient(45deg, var(--theme-page-background), var(--theme-page-background) 12px, var(--theme-card-border) 12px, var(--theme-card-border) 24px)',
                    }}
                  >
                    <span
                      className="w-9 h-9 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'var(--theme-primary)' }}
                    >
                      <Icon name="home" className="w-4 h-4" style={{ color: '#ffffff' }} />
                    </span>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <Icon
                    name="map-pin"
                    className="w-4 h-4 mt-0.5 flex-none"
                    style={{ color: 'var(--theme-primary)' }}
                  />
                  <div className="min-w-0 text-sm">
                    <p style={{ color: 'var(--theme-card-text)' }}>
                      {property.address.street}
                      {property.address.number ? `, ${property.address.number}` : ''}
                      {property.address.complement ? ` - ${property.address.complement}` : ''}
                    </p>
                    <p style={{ color: 'var(--theme-muted-text)' }}>
                      {property.address.neighborhood} - {property.address.city}/
                      {property.address.state}
                    </p>
                    <p style={{ color: 'var(--theme-muted-text)' }}>CEP: {property.address.zipCode}</p>
                  </div>
                </div>
              </Section>
            </div>

            {/* Sidebar */}
            <aside className="flex flex-col gap-4 md:gap-6 min-w-0">
              {/* Oportunidades */}
              <Section icon="dollar-sign" title="Oportunidades de Negócio" testId="property-opportunities">
                {property.opportunities && property.opportunities.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    {property.opportunities.map((opportunity) => (
                      <OpportunityCard key={opportunity.id} opportunity={opportunity} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                    Nenhuma oportunidade cadastrada.
                  </p>
                )}
              </Section>

              {/* Tipo & Referência */}
              <Section icon="info" title="Tipo & Referência" testId="property-type-reference">
                <div className="flex flex-col">
                  <DlRow label="Tipo">{getPropertyTypeLabel(property.propertyType)}</DlRow>
                  {property.reference && (
                    <DlRow label="Referência" divider>
                      <span style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
                        {property.reference}
                      </span>
                      <button
                        onClick={() => copyToClipboard(property.reference!)}
                        className="w-6 h-6 rounded-md inline-flex items-center justify-center transition-colors"
                        title="Copiar referência"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        data-testid="property-copy-reference-row"
                      >
                        <Icon
                          name="copy"
                          className="w-3.5 h-3.5"
                          style={{ color: 'var(--theme-muted-text)' }}
                        />
                      </button>
                    </DlRow>
                  )}
                  <DlRow label="Status" divider>
                    <span style={{ color: itemStatusColor.text }}>
                      {getItemStatusLabel(property.itemStatus)}
                    </span>
                  </DlRow>
                </div>
              </Section>

              {/* Construção */}
              {hasConstruction && (
                <Section icon="home" title="Construção" testId="property-construction">
                  <div className="flex flex-col">
                    {property.construction!.year && (
                      <DlRow label="Ano">{property.construction!.year}</DlRow>
                    )}
                    {property.construction!.condition && (
                      <DlRow label="Condição" divider={!!property.construction!.year}>
                        {getConditionLabel(property.construction!.condition)}
                      </DlRow>
                    )}
                  </div>
                </Section>
              )}

              {/* Tags */}
              {property.tags && property.tags.length > 0 && (
                <Section icon="tag" title="Tags" testId="property-tags">
                  <div className="flex flex-wrap gap-2">
                    {property.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2.5 py-1 rounded-full text-xs font-semibold"
                        style={{
                          backgroundColor: 'var(--theme-primary-background)',
                          color: 'var(--theme-primary)',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </Section>
              )}

              {/* Metadados */}
              <Section icon="clock" title="Informações" testId="property-metadata">
                <div className="flex flex-col">
                  <DlRow label="Criado em">
                    {new Date(document.createdAt).toLocaleDateString('pt-BR')}
                  </DlRow>
                  <DlRow label="Atualizado" divider>
                    {new Date(document.updatedAt).toLocaleDateString('pt-BR')}
                  </DlRow>
                  {document.metadata?.source && (
                    <DlRow label="Origem" divider>
                      {document.metadata.source === 'manual'
                        ? 'Manual'
                        : document.metadata.source === 'integration'
                          ? 'Integração'
                          : document.metadata.source === 'import'
                            ? 'Importação'
                            : 'API'}
                    </DlRow>
                  )}
                </div>
              </Section>

              {/* Informações internas — só equipe; renderiza apenas quando preenchido */}
              {property.internalDescription && (
                <Section icon="lock" title="Informações internas" testId="property-internal-info">
                  <p
                    className="text-sm whitespace-pre-wrap break-words"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {property.internalDescription}
                  </p>
                </Section>
              )}
            </aside>
          </div>
        </div>
      </div>

      {/* Lightbox de imagem em tela cheia (overlay escuro — visualização de mídia) */}
      {isImageFullscreen && hasPhotos && createPortal((
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
          onClick={() => setIsImageFullscreen(false)}
          data-testid="property-lightbox"
        >
          <button
            onClick={() => setIsImageFullscreen(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 text-white hover:bg-white/20 transition-colors"
            data-testid="property-lightbox-close"
            aria-label="Fechar"
          >
            <Icon name="x" className="w-6 h-6" />
          </button>

          <img
            src={photos[currentImageIndex]}
            alt={`${property.title} - Foto ${currentImageIndex + 1}`}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevImage();
                }}
                className="absolute left-4 w-12 h-12 rounded-full flex items-center justify-center bg-white/10 text-white hover:bg-white/20 transition-colors"
                data-testid="property-lightbox-prev"
                aria-label="Foto anterior"
              >
                <Icon name="chevron-left" className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextImage();
                }}
                className="absolute right-4 w-12 h-12 rounded-full flex items-center justify-center bg-white/10 text-white hover:bg-white/20 transition-colors"
                data-testid="property-lightbox-next"
                aria-label="Próxima foto"
              >
                <Icon name="chevron-right" className="w-8 h-8" />
              </button>
            </>
          )}

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-semibold">
            {currentImageIndex + 1} / {photos.length}
          </div>
        </div>
      ), window.document.body)}
    </div>
  );
};

/**
 * Card de oportunidade de negócio (venda/aluguel/etc.)
 */
const OpportunityCard: React.FC<{ opportunity: BusinessOpportunity }> = ({ opportunity }) => {
  const statusColor = getOpportunityStatusColor(opportunity.status);

  const periodLabel =
    opportunity.pricing.period === 'day'
      ? '/ dia'
      : opportunity.pricing.period === 'month'
        ? '/ mês'
        : opportunity.pricing.period === 'year'
          ? '/ ano'
          : '';

  const flags: string[] = [];
  if (opportunity.conditions?.negotiable) flags.push('Negociável');
  if (opportunity.conditions?.acceptsExchange) flags.push('Aceita permuta');
  if (opportunity.conditions?.installments?.available) {
    flags.push(`Parcela em até ${opportunity.conditions.installments.maxInstallments}x`);
  }

  const costsLine = (opportunity.additionalCosts || [])
    .map((cost) => {
      const freq =
        cost.frequency === 'monthly' ? '/mês' : cost.frequency === 'yearly' ? '/ano' : '';
      return `+ ${cost.name} ${formatPrice(cost.amount)}${freq}`;
    })
    .join(' · ');

  return (
    <div
      className="rounded-xl p-3"
      style={{ border: '1px solid var(--theme-card-border)' }}
      data-testid={`property-opportunity-${opportunity.id}`}
    >
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold mb-1.5"
        style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
      >
        {getOpportunityTypeLabel(opportunity.type)} · {getOpportunityStatusLabel(opportunity.status)}
      </span>

      <div className="flex items-baseline gap-1">
        <span
          className="text-lg font-extrabold tracking-tight"
          style={{ color: 'var(--theme-card-text)' }}
        >
          {formatFullPrice(opportunity.pricing.amount)}
        </span>
        {periodLabel && (
          <span className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }}>
            {periodLabel}
          </span>
        )}
      </div>

      {flags.length > 0 && (
        <div className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
          {flags.join(' · ')}
        </div>
      )}

      {costsLine && (
        <div
          className="text-xs mt-2 pt-2"
          style={{
            color: 'var(--theme-muted-text)',
            borderTop: '1px solid var(--theme-card-border)',
          }}
        >
          {costsLine}
        </div>
      )}
    </div>
  );
};
